import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Plus, 
  Filter, 
  SortAsc, 
  SortDesc, 
  ChevronLeft, 
  ChevronRight,
  // Truck removed - using image asset instead
  MapPin,
  Building,
  Activity,
  Eye,
  Bug, // Icono para el modo de depuración
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trucksService } from '../services/trucksService';
import MaintenanceDebugger from '../components/MaintenanceDebugger';

const TrucksPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Estados
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('numero_economico');
  const [sortOrder, setSortOrder] = useState('ASC');
  // La página se deriva de los searchParams: la URL es la única fuente de verdad
  const page = Number(searchParams.get('page') ?? 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    estatus: '',
    empresa: '',
    base: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showDebugger, setShowDebugger] = useState(false); // Para mostrar/ocultar el depurador de mantenimientos

  // Si la URL no tiene page al montar, restaurarla desde sessionStorage (solo una vez)
  useEffect(() => {
    try {
      if (!searchParams.has('page')) {
        const saved = sessionStorage.getItem('trucks_page');
        if (saved) {
          setSearchParams({ page: saved }, { replace: true });
        }
      }
    } catch (e) {
      console.warn('Error restaurando trucks_page desde sessionStorage', e);
    }
  }, []);

  // Guardar la página actual en sessionStorage cuando cambie (page es la fuente de verdad)
  useEffect(() => {
    try {
      sessionStorage.setItem('trucks_page', String(page));
    } catch (e) {
      console.warn('No se pudo guardar trucks_page en sessionStorage', e);
    }
  }, [page]);

  // Cargar trucks
  const loadTrucks = async () => {
    try {
      setLoading(true);
      const params = {
        page: page,
        limit: 30,
        search: searchTerm,
        sortBy: sortField,
        sortOrder: sortOrder,
        ...filters
      };
      
      console.log('🚚 Cargando trucks con parámetros:', params);
      const response = await trucksService.getAll(params);
      console.log('📦 Respuesta completa del backend:', response);
      console.log('📊 Paginación:', response.pagination);
      
      if (response.success) {
        setTrucks(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalItems(response.pagination.totalItems);
        console.log('✅ totalPages actualizado a:', response.pagination.totalPages);
        console.log('✅ totalItems actualizado a:', response.pagination.totalItems);
        setError(null);
      }
    } catch (error) {
      console.error('❌ Error cargando trucks:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    loadTrucks();
  }, [page, searchTerm, sortField, sortOrder, filters]);

  // Handlers
  const handleSearch = (value) => {
    setSearchTerm(value);
    setSearchParams({ page: '1' });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortOrder('ASC');
    }
    setSearchParams({ page: '1' });
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
    setSearchParams({ page: '1' });
  };

  const clearFilters = () => {
    setFilters({
      estatus: '',
      empresa: '',
      base: ''
    });
    setSearchTerm('');
    setSearchParams({ page: '1' });
  };

  const handleTruckClick = (truck) => {
    navigate(`/trucks/${truck.id}?page=${page}`);
  };

  const handleAddTruck = () => {
    // Asegurar que la navegación a nueva unidad sea correcta
    console.log('🚚 Navegando a formulario de nueva unidad');
    // Usar reemplazar historial para evitar problemas con el botón de regreso
    navigate('/trucks/new', { replace: false });
  };

  // Función para obtener el color del estatus
  const getStatusColor = (estatus) => {
    switch (estatus) {
      case 'Activo':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Mantenimiento preventivo':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Mantenimiento correctivo':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'En mantenimiento': // Mantener compatibilidad con registros antiguos
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Baja':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Función para obtener el color de la empresa
  const getCompanyColor = (empresa) => {
    switch (empresa) {
      case 'TITSA':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'TGT':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'GTT':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading && trucks.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <p className="text-xl text-gray-600">Cargando flotilla...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="flex items-center justify-between mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3">
            <img src="/images/tractocamion.png" alt="Flotilla" className="w-8 h-8 object-contain text-orange-500" />
            <h1 className="text-3xl font-bold text-gray-800">Flotilla</h1>
            <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
              {totalItems} unidades
            </span>
            
            {/* Botón de depuración */}
            <button
              onClick={() => setShowDebugger(!showDebugger)}
              className={`ml-4 flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                showDebugger ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title="Mostrar/ocultar diagnóstico de mantenimientos"
            >
              <Bug className="w-3 h-3" />
              {showDebugger ? 'Ocultar diagnóstico' : 'Diagnosticar contadores'}
            </button>
          </div>
          
          <motion.button
            onClick={handleAddTruck}
            className="flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus className="w-5 h-5" />
            Agregar Unidad
          </motion.button>
        </motion.div>
        
        {/* Componente de diagnóstico */}
        {showDebugger && <MaintenanceDebugger />}

        {/* Barra de búsqueda y filtros */}
        <motion.div
          className="bg-white rounded-2xl shadow-xl p-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Búsqueda */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por número económico, placas, marca, modelo, empresa o base..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            
            {/* Botón de filtros */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                showFilters 
                  ? 'bg-orange-100 text-orange-800 border-orange-300' 
                  : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100'
              }`}
            >
              <Filter className="w-5 h-5" />
              Filtros
            </button>
          </div>

          {/* Panel de filtros */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg border"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estatus</label>
                  <select
                    value={filters.estatus}
                    onChange={(e) => handleFilterChange('estatus', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Todos los estatus</option>
                    {trucksService.getEstatusOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Empresa</label>
                  <select
                    value={filters.empresa}
                    onChange={(e) => handleFilterChange('empresa', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Todas las empresas</option>
                    {trucksService.getEmpresaOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Base</label>
                  <select
                    value={filters.base}
                    onChange={(e) => handleFilterChange('base', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Todas las bases</option>
                    {trucksService.getBaseOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-3 flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
                  >
                    Limpiar filtros
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Error */}
        {error && (
          <motion.div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p>Error: {error}</p>
          </motion.div>
        )}

        {/* Tabla de trucks */}
        <motion.div
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th 
                    className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('numero_economico')}
                  >
                    <div className="flex items-center gap-1">
                      Número Económico
                      {sortField === 'numero_economico' && (
                        sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('placas')}
                  >
                    <div className="flex items-center gap-1">
                      Placas
                      {sortField === 'placas' && (
                        sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('marca')}
                  >
                    <div className="flex items-center gap-1">
                      Marca/Modelo
                      {sortField === 'marca' && (
                        sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('empresa')}
                  >
                    <div className="flex items-center gap-1">
                      Empresa
                      {sortField === 'empresa' && (
                        sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('base')}
                  >
                    <div className="flex items-center gap-1">
                      Base
                      {sortField === 'base' && (
                        sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('estatus')}
                  >
                    <div className="flex items-center gap-1">
                      Estatus
                      {sortField === 'estatus' && (
                        sortOrder === 'ASC' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kilometraje
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {trucks.map((truck, index) => (
                  <motion.tr
                    key={truck.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleTruckClick(truck)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ backgroundColor: '#f9fafb' }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{truck.numero_economico}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-gray-900">{truck.placas || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-gray-900">{truck.marca}</div>
                      <div className="text-gray-500 text-sm">{truck.modelo}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getCompanyColor(truck.empresa)}`}>
                        <Building className="w-4 h-4 mr-1" />
                        {truck.empresa}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center text-gray-600">
                        <MapPin className="w-4 h-4 mr-1" />
                        {truck.base}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(truck.estatus)}`}>
                        <Activity className="w-4 h-4 mr-1" />
                        {truck.estatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-gray-900">{truck.km_actual?.toLocaleString() || '0'} km</div>
                      <div className="text-gray-500 text-sm">Próx: {truck.km_prox_servicio?.toLocaleString() || '0'} km</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTruckClick(truck);
                        }}
                        className="text-orange-600 hover:text-orange-900 p-2 rounded-lg hover:bg-orange-50"
                        title="Ver detalles"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Mostrando {((page - 1) * 30) + 1} - {Math.min(page * 30, totalItems)} de {totalItems} unidades
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const newPage = Math.max(1, page - 1);
                    setSearchParams({ page: String(newPage) });
                  }}
                  disabled={page === 1}
                  className="p-2 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <span className="px-4 py-2 text-sm">
                  Página {page} de {totalPages}
                </span>
                
                <button
                  onClick={() => {
                    const newPage = Math.min(totalPages, page + 1);
                    setSearchParams({ page: String(newPage) });
                  }}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default TrucksPage;
